import React, { createContext, useContext, useMemo, useCallback, ReactNode } from 'react';

// Type definitions
interface Slot {
  id: string;
  selected: string | null;
}

interface Selections {
  slots: Slot[];
}

interface UpdateSelectionsOptions {
  slotId: string;
  itemSelected: string | null;
}

interface SelectionContextValue {
  selections: Selections;
  updateSelections: (opts: UpdateSelectionsOptions) => void;
}

interface SelectionProviderProps {
  children: ReactNode;
  selectedVariantId: string | null;
  onSelectVariant: (variantId: string | null) => void;
}

// Create context with undefined default (will be checked in hook)
const SelectionContext = createContext<SelectionContextValue | undefined>(undefined);

/**
 * Hook to access the SelectionContext.
 * @throws {Error} When used outside of SelectionProvider.
 */
export const useSelectionContext = (): SelectionContextValue => {
  const ctx = useContext(SelectionContext);
  if (!ctx) {
    throw new Error('useSelectionContext must be used within a SelectionProvider');
  }
  return ctx;
};

/**
 * Provider component for selection context.
 * Manages gift variant selection state.
 */
export const SelectionProvider: React.FC<SelectionProviderProps> = ({ 
  children, 
  selectedVariantId, 
  onSelectVariant 
}) => {
  const updateSelections = useCallback(({ slotId, itemSelected }: UpdateSelectionsOptions): void => {
    if (slotId === 'gift') {
      onSelectVariant(itemSelected);
    }
  }, [onSelectVariant]);

  const value = useMemo<SelectionContextValue>(() => ({
    selections: { 
      slots: [{ 
        id: 'gift', 
        selected: selectedVariantId 
      }] 
    },
    updateSelections
  }), [selectedVariantId, updateSelections]);

  return (
    <SelectionContext.Provider value={value}>
      {children}
    </SelectionContext.Provider>
  );
};